import React, { useEffect, useRef, useState } from 'react'; // React와 훅(useEffect, useRef, useState) 임포트
import '../styles/components/AddressSearch.css';

// 모듈(파일) 스코프에 로더 Promise를 캐싱할 변수 선언(한 번만 로드하기 위함)
let postcodePromise; // 카카오(다음) 우편번호 스크립트 로딩 상태를 저장

// 우편번호 스크립트를 1회만 로드하는 함수
function loadPostcodeScript() { // 필요 시 호출되며, 이미 로드 중/완료면 같은 Promise를 재사용
  if (typeof window === 'undefined') { // SSR 환경 대비: window가 없으면 에러
    return Promise.reject(new Error('window is not available'));
  }
  if (window.daum?.Postcode) { // 이미 스크립트가 로드되어 Postcode가 존재하면
    return Promise.resolve(); // 즉시 resolved Promise 반환
  }
  if (postcodePromise) { // 로딩 중인 Promise가 이미 있으면
    return postcodePromise; // 그 Promise를 그대로 반환(중복 삽입 방지)
  }
  // 처음 로드하는 경우: <script> 태그를 동적으로 추가하고 그 완료를 Promise로 감싼다
  postcodePromise = new Promise((resolve, reject) => { // 외부 스크립트 로딩을 Promise로 표현
    const s = document.createElement('script'); // 동적 스크립트 태그 생성
    s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'; // 카카오(다음) 우편번호 스크립트 주소
    s.async = true; // 비동기 로드
    s.onload = () => resolve(); // 로드 성공 시 resolve 호출
    s.onerror = () => reject(new Error('postcode script load failed')); // 로드 실패 시 reject
    document.body.appendChild(s); // 문서에 스크립트 삽입 → 다운로드 시작
  });
  return postcodePromise; // 생성한 Promise 반환(이후 호출은 이걸 재사용)
}

// 주소 검색 컴포넌트: 부모에서 formData, setFormData, loading을 넘겨받는다
function AddressSearch({ formData, setFormData, loading }) { // 주소 3칸(우편번호/주소/상세주소) UI 제공
  const detailRef = useRef(null); // 상세주소 입력창 DOM 참조(검색 완료 후 자동 포커스)
  const [addressLoading, setAddressLoading] = useState(false); // 우편번호 스크립트 로딩/팝업 준비 상태

  useEffect(() => { // 최초 마운트 시 스크립트를 미리 로드(첫 클릭 지연 방지, 실패해도 앱은 계속 동작)
    loadPostcodeScript().catch(() => {}); // 에러는 무시(사용자가 버튼 클릭 시 다시 시도됨)
  }, []); // 한 번만 실행

  const openAddressSearch = async () => { // "주소 검색" 버튼 클릭 시 실행
    setAddressLoading(true); // 버튼 상태를 로딩으로 표시
    try {
      await loadPostcodeScript(); // 스크립트 준비(이미 로드됐다면 즉시 통과)
      
      // 팝업 크기 설정
      const popupWidth = 500;
      const popupHeight = 600;
      
      new window.daum.Postcode({ // 우편번호 검색 팝업 생성
        oncomplete: (data) => { // 사용자가 주소를 선택했을 때 호출
          const addr = data.roadAddress || data.jibunAddress; // 도로명 주소 우선, 없으면 지번 주소 사용
          setFormData({ // 부모의 폼 상태 업데이트
            ...formData, // 기존 필드 유지
            postcode: data.zonecode, // 우편번호
            address: addr // 기본주소
          });
          setTimeout(() => detailRef.current?.focus(), 0); // 렌더 완료 직후 상세주소 입력으로 포커스 이동
        },
        width: popupWidth,
        height: popupHeight,
        left: (window.innerWidth / 2) - (popupWidth / 2),
        top: (window.innerHeight / 2) - (popupHeight / 2)
      }).open(); // 팝업 열기
    } finally {
      setAddressLoading(false); // 로딩 상태 해제(성공/실패 모두)
    }
  };

  return ( // 화면에 표시할 입력 UI
    <>
      <div className="address-search__postcode-group"> {/* 우편번호와 버튼을 가로로 배치 */}
        <input
          name="postcode" // 필드 이름(우편번호)
          placeholder="우편번호" // 입력 힌트
          value={formData.postcode || ''} // 현재 우편번호 값 표시
          onChange={() => {}} // 직접 타이핑 불가(검색으로만 입력)
          disabled // 비활성화(읽기 전용)
          required // 폼 필수 항목
          className="address-search__input" // 옆 버튼과 공간 분배
        />
        <button
          type="button" // 일반 버튼(폼 제출 아님)
          onClick={openAddressSearch} // 클릭 시 주소검색 팝업 오픈
          disabled={loading || addressLoading} // 전체 로딩/주소 로딩 중엔 비활성화
          className="address-search__button"
        >
          주소 검색 {/* 버튼 라벨 */}
        </button>
      </div>

      <div className="address-search__field"> {/* 기본주소 입력(검색 결과만 반영) */}
        <input
          name="address" // 필드 이름(기본주소)
          placeholder="주소" // 입력 힌트
          value={formData.address || ''} // 현재 주소 값 표시
          onChange={() => {}} // 직접 타이핑 불가(검색으로만 입력)
          disabled // 비활성화(읽기 전용)
          required // 폼 필수 항목
          className="address-search__input"
        />
      </div>

      <div className="address-search__field"> {/* 상세주소 입력(사용자 직접 입력) */}
        <input
          ref={detailRef} // 자동 포커스 대상
          name="detailAddress" // 필드 이름(상세주소)
          placeholder="상세주소" // 입력 힌트
          value={formData.detailAddress || ''} // 현재 상세주소 값
          onChange={(e) => setFormData({ ...formData, detailAddress: e.target.value })} // 타이핑 시 상태 반영
          required // 폼 필수 항목
          disabled={!formData.address || loading} // 주소 선택 전/로딩 중에는 비활성화
          className="address-search__input"
        />
      </div>
    </>
  );
}

export default AddressSearch; // 다른 파일에서 사용할 수 있도록 내보내기